/**
 * Los dos interruptores del club sobre lo que pueden hacer los jugadores
 * DENTRO de un partido: escribirse por WhatsApp, y anotar el marcador.
 *
 * VAN JUNTOS Y AL LADO DE LOS TORNEOS porque las dos preguntas se hacen en el
 * mismo momento —cuando el club decide cuánto suelta el control del torneo—
 * y ninguna se entiende sola en una pantalla de configuración aparte.
 *
 * CADA UNO SE MANDA SOLO. Si el guardado llevara los dos, dos personas
 * administrando el mismo club se pisarían: quien apaga uno volvería a
 * escribir el otro con el valor que tenía en su pantalla, que puede ser
 * viejo.
 */
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import type { Community, SetMatchSettingsRequest } from '@tennisup/contracts';

import { Alert } from './Alert';
import { api } from '../lib/api';
import { errorMessage } from '../lib/errorMessage';

export function MatchSettings({
  community,
  onChanged,
}: {
  community: Community;
  onChanged: (community: Community) => void;
}): JSX.Element {
  const { t } = useTranslation();
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  async function guardar(values: SetMatchSettingsRequest): Promise<void> {
    setBusy(true);
    setError(null);
    try {
      onChanged(await api.setMatchSettings(community.id, values));
    } catch (caught) {
      setError(errorMessage(t, caught));
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="auto-approve">
      <label className="check">
        <input
          type="checkbox"
          checked={community.allowWhatsappContact}
          disabled={busy}
          onChange={(e) => void guardar({ allowWhatsappContact: e.target.checked })}
        />
        <span>{t('matchSettings.whatsapp')}</span>
      </label>
      <p className="help">
        {community.allowWhatsappContact
          ? t('matchSettings.whatsappOn')
          : t('matchSettings.whatsappOff')}
      </p>

      <label className="check">
        <input
          type="checkbox"
          checked={community.allowPlayerScores}
          disabled={busy}
          onChange={(e) => void guardar({ allowPlayerScores: e.target.checked })}
        />
        <span>{t('matchSettings.playerScores')}</span>
      </label>
      <p className="help">
        {community.allowPlayerScores
          ? t('matchSettings.playerScoresOn')
          : t('matchSettings.playerScoresOff')}
      </p>

      {/*
        EL DEL CUMPLEAÑOS VA CON LOS OTROS DOS aunque no sea de un partido.
        Los tres son lo mismo para quien administra: cosas que el club decide
        una vez y que después pasan solas. Una pantalla aparte para un solo
        interruptor es una pantalla que nadie encuentra.
      */}
      <label className="check">
        <input
          type="checkbox"
          checked={community.celebrateBirthdays}
          disabled={busy}
          onChange={(e) => void guardar({ celebrateBirthdays: e.target.checked })}
        />
        <span>{t('matchSettings.birthdays')}</span>
      </label>
      <p className="help">
        {community.celebrateBirthdays
          ? t('matchSettings.birthdaysOn')
          : t('matchSettings.birthdaysOff')}
      </p>

      {error !== null && <Alert kind="error">{error}</Alert>}
    </div>
  );
}
